import appConstants from '@/plugins/constants';
import { exportTasklistExcel } from '@/modules/financialclose/utils/tasklistUtils';
import {
  AUTO_APPROVE,
  EVIDENCE_REQUIRED,
  SIGN_OFF,
  USER_ROLE,
  coerceEvidenceRequired,
  isEvidenceRequiredEnabled,
} from '@/modules/financialclose/utils/constants';
import {
  CHECKLIST_VALIDATION_MESSAGES,
  canonicalizeBulkTaskHeader,
  findDuplicateValues,
  duplicateInRoleMessage,
} from '@/modules/financialclose/utils/bulkUploadValidationMessages';
import { groupLocationsByAssignmentNumber } from './assignmentGrouping';
import { assignmentsFromTemplate } from './taskListPayload';
import { splitLocationNames } from '../../../../utils/locationName';
import {
  BULK_IMPORT_MODES,
  BULK_IMPORT_ROW_SYSTEM_KEYS,
  BULK_MERGE_SCALAR_KEYS,
  getBulkEditTaskTemplateInstructions,
  EXPORT_TASK_NAME_MAX_WIDTH,
} from './taskListConstants';

const FALLBACK_TASK_TYPES = appConstants.TASK_LIST_CONSTANTS.TASK_TYPES;


/** Bulk import (blank template) TOP SHEET headers. */
export function getBulkImportExcelHeaders(locationHeader = 'Location') {
  return [
    'Task Name',
    'Category',
    'Sub Category',
    locationHeader || 'Location',
    'Assertions',
    'Purpose',
    'Instruction',
    'Preparer',
    'Approver',
    SIGN_OFF.APPROVER_LABEL,
    'Frequency',
    'Due Days',
    'Account No',
  ];
}

/** @deprecated Prefer getBulkImportExcelHeaders(locationHeader). */
export const BULK_IMPORT_EXCEL_HEADERS = getBulkImportExcelHeaders();

export const BULK_IMPORT_OPTIONAL_HEADERS = [
  'Priority',
  'No. of Evidences',
  EVIDENCE_REQUIRED.EXCEL_HEADER,
  'Enforce Threshold',
  AUTO_APPROVE.EXCEL_HEADER,
];

/**
 * Bulk-edit Excel columns (download + upload) — same order as legacy exportTasklistExcel.
 */
export function getBulkEditExcelHeaders(locationHeader = 'Location') {
  return [
    'Reference',
    'Task Name',
    'Due Days',
    'Category',
    'Sub Category',
    'Priority',
    'Preparer',
    'Approver',
    SIGN_OFF.APPROVER_LABEL,
    'Purpose',
    'Instruction',
    'Frequency',
    'No. of Evidences',
    EVIDENCE_REQUIRED.EXCEL_HEADER,
    'Account No',
    locationHeader || 'Location',
    'Assertions',
    'Enforce Threshold',
    AUTO_APPROVE.EXCEL_HEADER,
  ];
}

/** @deprecated Prefer getBulkEditExcelHeaders(locationHeader). */
export const BULK_EDIT_EXCEL_HEADERS = getBulkEditExcelHeaders();

/** Preview headers for Bulk Edit — mirrors Excel columns exactly. */
export function getBulkEditPreviewHeaders(locationHeader = 'Location') {
  return [
    { text: 'Reference', value: 'ref' },
    { text: 'Task Name', value: 'description' },
    { text: 'Due Days', value: 'duedays' },
    { text: 'Category', value: 'category' },
    { text: 'Sub Category', value: 'subcategory' },
    { text: 'Priority', value: 'priority' },
    { text: 'Preparer', value: USER_ROLE.PREPARER },
    { text: 'Approver', value: USER_ROLE.APPROVER },
    { text: SIGN_OFF.APPROVER_LABEL, value: USER_ROLE.SIGN_OFF_APPROVER },
    { text: 'Purpose', value: 'purpose' },
    { text: 'Instruction', value: 'instruction' },
    { text: 'Frequency', value: 'tags' },
    { text: 'No. of Evidences', value: 'evidences' },
    { text: EVIDENCE_REQUIRED.EXCEL_HEADER, value: 'evidence_required' },
    { text: 'Account No', value: 'glcode' },
    { text: locationHeader || 'Location', value: 'location' },
    { text: 'Assertions', value: 'assertions' },
    { text: 'Enforce Threshold', value: 'enforceThreshold' },
    { text: AUTO_APPROVE.EXCEL_HEADER, value: 'auto_approve' },
  ];
}

/** @deprecated Prefer getBulkEditPreviewHeaders(locationHeader). */
export const BULK_EDIT_PREVIEW_HEADERS = getBulkEditPreviewHeaders();

/** Preview headers for Bulk Import — Reference is preview-only (from bulkvalidate rowMeta). */
export function getBulkImportPreviewHeaders(locationHeader = 'Location') {
  return [
    { text: 'Reference', value: 'ref' },
    { text: 'Task Name', value: 'description' },
    { text: 'Category', value: 'category' },
    { text: 'Sub Category', value: 'subcategory' },
    { text: locationHeader || 'Location', value: 'location' },
    { text: 'Assertions', value: 'assertions' },
    { text: 'Purpose', value: 'purpose' },
    { text: 'Instruction', value: 'instruction' },
    { text: 'Preparer', value: USER_ROLE.PREPARER },
    { text: 'Approver', value: USER_ROLE.APPROVER },
    { text: SIGN_OFF.APPROVER_LABEL, value: USER_ROLE.SIGN_OFF_APPROVER },
    { text: 'Frequency', value: 'tags' },
    { text: 'Due Days', value: 'duedays' },
    { text: 'Account No', value: 'glcode' },
    { text: 'Priority', value: 'priority' },
    { text: 'No. of Evidences', value: 'evidences' },
    { text: EVIDENCE_REQUIRED.EXCEL_HEADER, value: 'evidence_required' },
    { text: 'Enforce Threshold', value: 'enforceThreshold' },
    { text: AUTO_APPROVE.EXCEL_HEADER, value: 'auto_approve' },
  ];
}

/** @deprecated Prefer getBulkImportPreviewHeaders(locationHeader). */
export const BULK_IMPORT_PREVIEW_HEADERS = getBulkImportPreviewHeaders();

export const BULK_IMPORT_ERROR_FOOTER =
  '*We found some errors, please rectify the same to proceed';

/** Non-system custom columns that have a columnName (active for import/export). */
function getActiveCustomColumns(customColumns = []) {
  return (customColumns || []).filter((c) => c && !c.isSystem && c.columnName);
}

/** Excel header text for a custom column (date columns get dd/mm/yyyy suffix). */
export function customColumnHeaderText(col = {}) {
  const name = col.displayName || col.displayText || col.columnName || '';
  return col.dataType === 'date' ? `${name} (dd/mm/yyyy)` : name;
}

/**
 * Preview table headers = Excel template columns + custom fields (no extras).
 */
export function buildBulkPreviewHeaders(
  mode = BULK_IMPORT_MODES.IMPORT,
  customColumns = [],
  locationHeader = 'Location'
) {
  const base =
    mode === BULK_IMPORT_MODES.BULK_EDIT
      ? getBulkEditPreviewHeaders(locationHeader)
      : getBulkImportPreviewHeaders(locationHeader);
  const custom = getActiveCustomColumns(customColumns).map((c) => ({
    text: customColumnHeaderText(c),
    value: c.columnName,
    visible: true,
    sortable: false,
    filterable: false,
  }));
  return [
    ...base.map((h) => ({
      ...h,
      visible: true,
      sortable: false,
      filterable: false,
    })),
    ...custom,
  ];
}

/** Normalize Excel header text for loose matching (trim, lower, strip date suffix). */
function normalizeHeaderKey(header = '') {
  return String(header || '')
    .trim()
    .toLowerCase()
    .replace(/\s*\(dd\/mm\/yyyy\)\s*$/i, '')
    .trim();
}

/**
 * Find the Excel column index for a custom field using displayName / columnName aliases.
 * @returns {number} index in headerRow, or -1 when not found
 */
function findHeaderIndex(headerRow = [], col = {}) {
  const candidates = [
    customColumnHeaderText(col),
    col.displayName,
    col.displayText,
    col.columnName,
  ]
    .map((h) => normalizeHeaderKey(h))
    .filter(Boolean);

  return headerRow.findIndex((h) => candidates.includes(normalizeHeaderKey(h)));
}

/**
 * Read custom-field cell values from one Excel data row.
 * @returns {{ values: Object, customfields: Object[] }} flat map + API customfields array
 */
function readCustomFieldValues(rawRow = [], headerRow = [], customColumns = []) {
  const values = {};
  const customfields = [];
  getActiveCustomColumns(customColumns).forEach((col) => {
    const idx = findHeaderIndex(headerRow, col);
    let value = idx >= 0 ? rawRow[idx] : '';
    if (value == null) value = '';
    if (typeof value === 'object' && value instanceof Date) {
      value = value.toLocaleDateString('en-GB');
    } else {
      value = String(value).trim();
    }
    values[col.columnName] = value;
    customfields.push({ [col.columnName]: value });
  });
  return { values, customfields };
}

/** Resolve a custom column value from a stored template (top-level or customfields[]). */
function getTemplateCustomValue(template = {}, col = {}) {
  if (!col?.columnName) return '';
  if (template[col.columnName] != null && template[col.columnName] !== '') {
    return template[col.columnName];
  }
  if (Array.isArray(template.customfields)) {
    for (const entry of template.customfields) {
      if (!entry || typeof entry !== 'object') continue;
      if (Object.prototype.hasOwnProperty.call(entry, col.columnName)) {
        return entry[col.columnName];
      }
      if (entry.columnName === col.columnName && entry.value != null) {
        return entry.value;
      }
    }
  }
  return '';
}

/** Merge flat custom values (+ customfields[]) onto a parsed row. */
function applyCustomFieldValues(row = {}, customValues = {}, customfields = []) {
  const next = { ...row, ...customValues };
  if (customfields.length) next.customfields = customfields;
  return next;
}

/** Split a comma-separated cell into trimmed non-empty strings. */
function splitCsv(value) {
  if (value == null || value === '') return [];
  return String(value)
    .split(',')
    .map((s) => s.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

/**
 * Build primary + overflow metadata for multi-value preview cells (location, users, tags).
 * @returns {{ primary: string, overflowCount: number, overflowItems: string[], display: string }}
 */
function buildOverflow(values = []) {
  const list = (values || []).filter(Boolean);
  if (!list.length) {
    return { primary: '—', overflowCount: 0, overflowItems: [], display: '—' };
  }
  return {
    primary: list[0],
    overflowCount: Math.max(0, list.length - 1),
    overflowItems: list.slice(1),
    display: list.join(', '),
  };
}

/** Trim every cell in an Excel header row to strings. */
function normalizeHeaderRow(row = []) {
  return (row || []).map((cell) => String(cell || '').trim());
}

/**
 * True when a header is the task-name column (canonical "Task Name",
 * plus legacy "Description" / "Task Description").
 */
function isTaskNameHeader(header = '') {
  return canonicalizeBulkTaskHeader(header) === 'Task Name';
}

/** Like exact header match, with Task Name / Frequency aliases for legacy files. */
function importHeadersMatch(actual, expected) {
  if (!actual?.length || actual.length < expected.length) return false;
  return expected.every(
    (h, i) => canonicalizeBulkTaskHeader(actual[i]) === canonicalizeBulkTaskHeader(h)
  );
}

/** Accept either the custom LocationHeader label or the legacy "Location" column title. */
function importHeadersMatchWithLocationAlias(actual, getExpectedHeaders, locationHeader = 'Location') {
  const loc = locationHeader || 'Location';
  if (importHeadersMatch(actual, getExpectedHeaders(loc))) return true;
  if (loc !== 'Location' && importHeadersMatch(actual, getExpectedHeaders('Location'))) {
    return true;
  }
  return false;
}

/** Blank or N/A on an Account Reconciliation row means enforced (Yes). Explicit No is off. */
function parseEnforceThresholdCell(raw) {
  const text = String(raw ?? '').trim().toLowerCase();
  if (['no', 'false', '0'].includes(text)) return false;
  return true;
}

/** Parse Yes/No/True/1 cell values into boolean. */
function parseYesNoBoolean(raw) {
  return !!String(raw || '')
    .trim()
    .toLowerCase()
    .match(/^(true|yes|1)$/);
}

/** Column index by header label, or -1 when the sheet does not include it. */
function headerIndexByLabel(headerRow = [], label = '') {
  const target = normalizeHeaderKey(label);
  return headerRow.findIndex((cell) => normalizeHeaderKey(cell) === target);
}

function cellAt(row = [], index) {
  if (index == null || index < 0) return '';
  return row[index];
}

/**
 * Evidence count plus Evidence Required flag.
 * Blank / Yes / missing column means required (same as single-task save).
 */
function readEvidenceCells(rawRow = [], headerRow = [], fallbackCountIndex, fallbackRequiredIndex) {
  const countIndex = headerIndexByLabel(headerRow, 'No. of Evidences');
  const requiredIndex = headerIndexByLabel(headerRow, EVIDENCE_REQUIRED.EXCEL_HEADER);
  const countRaw = cellAt(rawRow, countIndex >= 0 ? countIndex : fallbackCountIndex);
  const requiredRaw = cellAt(rawRow, requiredIndex >= 0 ? requiredIndex : fallbackRequiredIndex);
  const no_of_evidence = countRaw === '' || countRaw == null ? 0 : Number(countRaw);
  return {
    no_of_evidence: Number.isFinite(no_of_evidence) ? no_of_evidence : 0,
    evidence_required: coerceEvidenceRequired(requiredRaw),
  };
}

/** Join a list into a single comma-separated string (empty items dropped). */
function joinList(values = []) {
  return (values || []).filter(Boolean).join(',');
}

/** Coerce a cell/object into a display string using preferred keys. */
function cellValue(item, keys = ['glcode', 'name', 'label']) {
  if (item == null || item === '') return '';
  if (typeof item === 'string' || typeof item === 'number') return String(item).trim();
  if (typeof item === 'object') {
    for (const key of keys) {
      const val = item[key];
      if (val != null && val !== '') return String(val).trim();
    }
  }
  return '';
}

/** Unique trimmed GL account numbers from a mixed list/object input. */
function normalizeGlcodeList(glcodes = []) {
  const list = Array.isArray(glcodes) ? glcodes : glcodes != null ? [glcodes] : [];
  return [...new Set(list.map((g) => cellValue(g, ['glcode', 'name', '_id'])).filter(Boolean))];
}

/** Map user ids (or email objects) to emails/names for Excel export. */
function resolveUserEmails(ids = [], users = []) {
  const byId = new Map(
    (users || []).map((u) => [String(u._id || u.id), u.email || u.name || ''])
  );
  return (ids || [])
    .map((id) => {
      if (id == null) return '';
      if (typeof id === 'object') return id.email || id.name || '';
      return byId.get(String(id)) || String(id);
    })
    .filter(Boolean);
}

/** Resolve location ids to unique display names using the location master list. */
function resolveLocationNamesByIds(locationIds = [], locations = []) {
  const byId = new Map(
    (locations || []).map((l) => [
      String(l._id || l.id),
      l.name || l.location || l.description || '',
    ])
  );
  const names = [];
  (locationIds || []).forEach((id) => {
    const name = byId.get(String(id)) || '';
    if (name && !names.includes(name)) names.push(name);
  });
  return names;
}

/**
 * Index templates by ref and description for matching Excel rows to existing tasks.
 * @returns {{ byRef: Map, byDescription: Map }}
 */
function buildTemplateLookup(templates = []) {
  const byRef = new Map();
  const byDescription = new Map();
  (templates || []).forEach((t) => {
    const ref = String(t.ref || '').trim();
    const desc = String(t.description || '').trim().toLowerCase();
    if (ref) byRef.set(ref, t);
    if (desc && !byDescription.has(desc)) byDescription.set(desc, t);
  });
  return { byRef, byDescription };
}

/**
 * Detect whether a header row is bulk-edit (Reference) or blank import (Task Name / Description).
 * @returns {'bulkEdit'|'import'|'unknown'}
 */
function detectSheetFormat(headerRow = []) {
  const cells = (headerRow || []).map((h) =>
    String(h || '')
      .trim()
      .toLowerCase()
  );
  // exportTasklistExcel leaves column A empty — find first non-empty header
  const first = cells.find((c) => c) || '';
  if (first === 'reference' || first === 'ref') return 'bulkEdit';
  if (isTaskNameHeader(first)) return 'import';
  return 'unknown';
}

/** Column index of Reference/Ref in a bulk-edit header (0 if missing). */
function findBulkEditRefColumnIndex(headerRow = []) {
  const idx = (headerRow || []).findIndex((h) =>
    ['ref', 'reference'].includes(String(h || '').trim().toLowerCase())
  );
  return idx >= 0 ? idx : 0;
}

/** Scan the first rows of a sheet to find the header row index. */
function findHeaderRowIndex(sheetRows = []) {
  for (let i = 0; i < Math.min(sheetRows.length, 10); i += 1) {
    const format = detectSheetFormat(normalizeHeaderRow(sheetRows[i]));
    if (format !== 'unknown') return i;
  }
  return 0;
}

/** Preview display helper — empty values become an em dash. */
function displayOrDash(value) {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  return value;
}

/**
 * Build preview row with Excel fields (+ custom fields when present).
 */
export function buildPreviewRow(row = {}, customColumns = []) {
  const locationList = row.locationNames || row.location || [];
  const locationOverflow = buildOverflow(splitLocationNames(locationList));
  const tagsList = Array.isArray(row.tags) ? row.tags : splitCsv(row.tags);
  const preparerList = Array.isArray(row.preparer)
    ? row.preparer
    : Array.isArray(row._preparerEmails)
      ? row._preparerEmails
      : splitCsv(row.preparer);
  const approverList = Array.isArray(row.approver)
    ? row.approver
    : Array.isArray(row._approverEmails)
      ? row._approverEmails
      : splitCsv(row.approver);
  const signOffList = Array.isArray(row.sign_off_approver)
    ? row.sign_off_approver
    : Array.isArray(row.sign_off_approvers)
      ? row.sign_off_approvers
      : Array.isArray(row._signOffApproverEmails)
        ? row._signOffApproverEmails
        : splitCsv(row.sign_off_approver || row.sign_off_approvers);
  const glList = Array.isArray(row.glcode) ? row.glcode : splitCsv(row.glcode);
  const assertionsList = Array.isArray(row.assertions)
    ? row.assertions
    : splitCsv(row.assertions);
  const tagsOverflow = buildOverflow(tagsList);
  const assertionsOverflow = buildOverflow(assertionsList);
  const glOverflow = buildOverflow(glList);

  const preview = {
    ref: row.ref || '—',
    description: row.description || '—',
    duedays: row.duedays == null || row.duedays === '' ? '—' : row.duedays,
    category: row.category || '—',
    subcategory: row.subcategory || '—',
    priority: row.priority || '—',
    preparer: displayOrDash(preparerList),
    preparerPrimary: buildOverflow(preparerList).primary,
    preparerOverflowCount: buildOverflow(preparerList).overflowCount,
    preparerOverflowItems: buildOverflow(preparerList).overflowItems,
    approver: displayOrDash(approverList),
    approverPrimary: buildOverflow(approverList).primary,
    approverOverflowCount: buildOverflow(approverList).overflowCount,
    approverOverflowItems: buildOverflow(approverList).overflowItems,
    sign_off_approver: displayOrDash(signOffList),
    sign_off_approverPrimary: buildOverflow(signOffList).primary,
    sign_off_approverOverflowCount: buildOverflow(signOffList).overflowCount,
    sign_off_approverOverflowItems: buildOverflow(signOffList).overflowItems,
    purpose: row.purpose || '—',
    instruction: row.instruction || '—',
    tags: tagsOverflow.display,
    tagsPrimary: tagsOverflow.primary,
    tagsOverflowCount: tagsOverflow.overflowCount,
    tagsOverflowItems: tagsOverflow.overflowItems,
    assertions: assertionsOverflow.display,
    assertionsPrimary: assertionsOverflow.primary,
    assertionsOverflowCount: assertionsOverflow.overflowCount,
    assertionsOverflowItems: assertionsOverflow.overflowItems,
    evidences:
      row.no_of_evidence == null && row.evidences == null
        ? '—'
        : row.no_of_evidence ?? row.evidences,
    evidence_required: isEvidenceRequiredEnabled(row.evidence_required) ? 'Yes' : 'No',
    glcode: glOverflow.display,
    glcodePrimary: glOverflow.primary,
    glcodeOverflowCount: glOverflow.overflowCount,
    glcodeOverflowItems: glOverflow.overflowItems,
    location: locationOverflow.display,
    locationPrimary: locationOverflow.primary,
    locationOverflowCount: locationOverflow.overflowCount,
    locationOverflowItems: locationOverflow.overflowItems,
    enforceThreshold: row.enforceThreshold ? 'Yes' : row.enforceThreshold === false ? 'No' : '—',
    auto_approve: row.auto_approve ? 'Yes' : row.auto_approve === false ? 'No' : '—',
  };

  getActiveCustomColumns(customColumns).forEach((col) => {
    const val = row[col.columnName];
    preview[col.columnName] = val == null || val === '' ? '—' : val;
  });

  return preview;
}

function listFieldValues(value) {
  if (Array.isArray(value)) return value.map((v) => String(v || '').trim()).filter(Boolean);
  if (value == null || value === '' || value === '—') return [];
  return splitCsv(value);
}

function mergeFieldErrors(base = {}, extra = {}) {
  const out = { ...base };
  Object.keys(extra || {}).forEach((key) => {
    const msg = extra[key];
    if (!msg) return;
    if (!out[key]) {
      out[key] = msg;
    } else if (!String(out[key]).includes(String(msg))) {
      out[key] = `${out[key]} ${msg}`.trim();
    }
  });
  return out;
}

/**
 * Merge parallel row-error maps (client preview + server) without dropping either side.
 * @param {Array<Object>} base
 * @param {Array<Object>} extra
 * @returns {Array<Object>}
 */
export function mergeBulkImportRowErrors(base = [], extra = []) {
  const len = Math.max((base || []).length, (extra || []).length);
  const merged = [];
  for (let i = 0; i < len; i += 1) {
    merged.push(mergeFieldErrors(base[i] || {}, extra[i] || {}));
  }
  return merged;
}

/**
 * Post-upload preview checks shared with project bulk add/edit:
 * duplicate emails in the same role list, and empty Location.
 * @param {Array<Object>} rows payload rows (arrays, not preview display strings)
 * @param {string} locationHeader
 * @returns {Array<Object>}
 */
export function getDuplicateRoleAndEmptyLocationErrors(rows = [], locationHeader = 'Location') {
  const locLabel = locationHeader || 'Location';
  return (rows || []).map((row) => {
    const errors = {};
    const location = asLocationArray(row.location || row.locationNames);
    if (!location.length) {
      errors.location = `${locLabel} is required`;
    }

    const preparer = listFieldValues(row.preparer || row._preparerEmails);
    const approver = listFieldValues(row.approver || row._approverEmails);
    const signOffApprover = listFieldValues(
      row.sign_off_approver || row.sign_off_approvers || row._signOffApproverEmails
    );

    const duplicatePreparers = findDuplicateValues(preparer);
    if (duplicatePreparers.length) {
      errors[USER_ROLE.PREPARER] = duplicateInRoleMessage(duplicatePreparers, 'preparer');
    }
    const duplicateApprovers = findDuplicateValues(approver);
    if (duplicateApprovers.length) {
      errors[USER_ROLE.APPROVER] = duplicateInRoleMessage(duplicateApprovers, 'approver');
    }
    const duplicateSignOff = findDuplicateValues(signOffApprover);
    if (duplicateSignOff.length) {
      errors[USER_ROLE.SIGN_OFF_APPROVER] = duplicateInRoleMessage(duplicateSignOff, 'sign-off approver');
    }
    return errors;
  });
}

/**
 * Validate preview/payload rows. Returns parallel errors array (keyed by column value).
 * Bulk Import & Bulk Edit both highlight invalid / missing cells.
 * Eligibility (inactive user, unknown email/location name) is enforced by bulkvalidate.
 */
export function validateBulkImportRows(
  rows = [],
  {
    mode = BULK_IMPORT_MODES.IMPORT,
    chartOfAccounts = [],
    categories = [],
    subcategories = [],
    taskTypes,
    locationHeader = 'Location',
  } = {}
) {
  const AR = taskTypes?.ACCOUNT_RECONCILIATION || 'Account Reconciliation';
  const VALID_PRIORITIES = ['High', 'Medium', 'Low'];

  const toLowerSet = (list = []) =>
    new Set(
      (list || [])
        .map((v) => String(v || '').trim().toLowerCase())
        .filter(Boolean)
    );

  const coaCategorySet = toLowerSet([
    ...(chartOfAccounts || []).map((c) => c?.category),
    ...(categories || []),
  ]);
  const coaSubcategorySet = toLowerSet([
    ...(chartOfAccounts || []).map((c) => c?.subcategory),
    ...(subcategories || []),
  ]);
  const coaGlcodes = new Set(
    (chartOfAccounts || []).map((c) => String(c?.glcode || '').trim()).filter(Boolean)
  );

  const asList = (value) => {
    if (Array.isArray(value)) return value.map((v) => String(v || '').trim()).filter(Boolean);
    if (value == null || value === '' || value === '—') return [];
    return splitCsv(value);
  };

  const appendError = (errors, field, message) => {
    if (!message) return;
    if (!errors[field]) {
      errors[field] = message;
    } else {
      errors[field] = `${errors[field]},${message}`;
    }
  };

  return (rows || []).map((row) => {
    const errors = {};
    const description = String(row.description || '').trim();
    const category = String(row.category || '').trim();
    const subcategory = String(row.subcategory || '').trim();
    const priority = String(row.priority || '').trim();
    const preparer = asList(row.preparer || row._preparerEmails);
    const approver = asList(row.approver || row._approverEmails);
    const signOffApprover = asList(
      row.sign_off_approver || row.sign_off_approvers || row._signOffApproverEmails
    );
    const tags = asList(row.tags);
    const glcode = asList(row.glcode).map((g) => String(g).trim()).filter(Boolean);
    const isAr =
      row.task_type === AR ||
      String(row.task_type || '').toLowerCase().includes('reconcil') ||
      glcode.length > 0;

    if (!description || description === '—') {
      errors.description = 'Task Name is required';
    }

    if (mode === BULK_IMPORT_MODES.BULK_EDIT && (!row.ref || row.ref === '—')) {
      errors.ref = 'Reference is required';
    }

    // Required fields — Import & Bulk Edit
    if (!category) errors.category = 'Category is required';
    if (!subcategory) errors.subcategory = 'Sub Category is required';
    if (!preparer.length) errors.preparer = CHECKLIST_VALIDATION_MESSAGES.PREPARER_REQUIRED;
    if (!approver.length) errors.approver = CHECKLIST_VALIDATION_MESSAGES.APPROVER_REQUIRED;
    if (!tags.length) errors.tags = CHECKLIST_VALIDATION_MESSAGES.TAGS_REQUIRED;

    // Preparer / approver / sign-off overlap
    if (preparer.length || approver.length || signOffApprover.length) {

      if (preparer.length && approver.length) {
        const duplicates = preparer.filter((p) =>
          approver.some((a) => a.toLowerCase() === p.toLowerCase())
        );
        if (duplicates.length) {
          appendError(
            errors,
            USER_ROLE.PREPARER,
            `'${duplicates.join(',')}' cannot be both preparer and approver`
          );
        }
      }

      if (signOffApprover.length && !approver.length) {
        errors[USER_ROLE.SIGN_OFF_APPROVER] = SIGN_OFF.ERRORS.ORPHAN_L2;
      }

      if (signOffApprover.length) {
        const preparerOverlap = signOffApprover.filter((email) =>
          preparer.some((p) => p.toLowerCase() === String(email).toLowerCase())
        );
        if (preparerOverlap.length) {
          appendError(errors, USER_ROLE.SIGN_OFF_APPROVER, SIGN_OFF.ERRORS.PREPARER_SOD);
        }
        const approverOverlap = signOffApprover.filter((email) =>
          approver.some((a) => a.toLowerCase() === String(email).toLowerCase())
        );
        if (approverOverlap.length) {
          appendError(errors, USER_ROLE.SIGN_OFF_APPROVER, SIGN_OFF.ERRORS.APPROVER_SOD);
        }
      }
    }

    if (priority && !VALID_PRIORITIES.includes(priority)) {
      errors.priority = 'Invalid priority. Allowed: High, Medium, Low';
    }

    if (row.duedays == null || row.duedays === '' || row.duedays === '—') {
      errors.duedays = 'Due Days is required';
    } else {
      const due = Number(row.duedays);
      if (!Number.isFinite(due)) {
        errors.duedays = 'Due Days must be a number';
      } else if (due < -200 || due > 200) {
        errors.duedays = 'Due Days must be between -200 and 200';
      }
    }

    const evidenceCount = Number(row.no_of_evidence ?? row.evidences ?? 0);
    const evidenceRequired = coerceEvidenceRequired(row.evidence_required);
    if (!Number.isFinite(evidenceCount) || evidenceCount < 0 || evidenceCount > 10) {
      errors.evidences = 'No. of Evidences must be between 0 and 10';
    } else if (isEvidenceRequiredEnabled(evidenceRequired) && evidenceCount < 1) {
      errors.evidences =
        'When Evidence Required is Yes, No. of Evidences must be between 1 and 10';
    }

    if (glcode.length && coaGlcodes.size > 0) {
      const invalidGl = glcode.filter((g) => !coaGlcodes.has(String(g)));
      if (invalidGl.length) {
        errors.glcode = invalidGl.join(', ') + ' Invalid GL Code';
      }
    }

    // AR tasks — invalid Category / Sub Category (Figma red-border state)
    if (isAr && coaCategorySet.size > 0) {
      if (category && !coaCategorySet.has(category.toLowerCase())) {
        errors.category = '"' + category + '" is not a valid COA category';
      }
      if (subcategory && !coaSubcategorySet.has(subcategory.toLowerCase())) {
        errors.subcategory = '"' + subcategory + '" is not a valid COA sub category';
      }
    }

    return mergeFieldErrors(
      errors,
      getDuplicateRoleAndEmptyLocationErrors([row], locationHeader)[0]
    );
  });
}

/** True when any row in the parallel errors array has at least one field error. */
export function hasBulkImportErrors(rowErrors = []) {
  return (rowErrors || []).some((e) => e && Object.keys(e).length > 0);
}

function getTemplateTags(template = {}) {
  const tags = template.tags || template.frequency || [];
  return (Array.isArray(tags) ? tags : [tags])
    .map((t) => cellValue(t, ['name', 'label']))
    .filter(Boolean);
}

/**
 * Build one Excel row for a single assignment block on a task template.
 */
export function buildBulkEditExportItemFromAssignment(
  template = {},
  assignment = {},
  { users = [], locations = [], customColumns = [], taskTypes } = {}
) {
  const AR = taskTypes?.ACCOUNT_RECONCILIATION || 'Account Reconciliation';
  const templateGlcodes = normalizeGlcodeList(template.glcode);
  const assignmentGlcodes = normalizeGlcodeList(assignment.glcodes);
  const glcodes = assignmentGlcodes.length ? assignmentGlcodes : templateGlcodes;
  const isAr =
    template.task_type === AR ||
    String(template.task_type || '').toLowerCase().includes('reconcil') ||
    glcodes.length > 0;

  const item = {
    ref: template.ref || '',
    description: template.description || '',
    duedays: template.duedays ?? template.due_days ?? '',
    category: template.category || '',
    subcategory: template.subcategory || '',
    priority: template.priority || '',
    preparerEmails: joinList(resolveUserEmails(assignment.preparer || [], users)),
    approverEmails: joinList(resolveUserEmails(assignment.approver || [], users)),
    signOffApproverEmails: joinList(
      resolveUserEmails(assignment.sign_off_approvers || [], users)
    ),
    purpose: template.purpose || '',
    instruction: template.instruction || '',
    tags: getTemplateTags(template),
    no_of_evidence: template.no_of_evidence ?? template.noofevidence ?? 0,
    evidence_required: coerceEvidenceRequired(template.evidence_required),
    glcode: glcodes,
    location: resolveLocationNamesByIds(assignment.location_ids, locations),
    assertions: Array.isArray(template.assertions)
      ? template.assertions.filter(Boolean)
      : splitCsv(template.assertion || template.assertions),
    enforceThreshold: !!(
      assignment.enforceThreshold ??
      template.enforceThreshold
    ),
    auto_approve: !!(
      assignment.auto_approve ??
      template.auto_approve
    ),
    task_type: template.task_type || (isAr ? AR : ''),
  };

  getActiveCustomColumns(customColumns).forEach((col) => {
    item[col.columnName] = getTemplateCustomValue(template, col) || '';
  });

  return item;
}

/**
 * Build one Excel-ready task item (first assignment only — prefer templateToBulkEditExportItems).
 */
export function templateToBulkEditExportItem(
  template = {},
  options = {}
) {
  const items = templateToBulkEditExportItems(template, options);
  if (items.length) return items[0];

  return buildBulkEditExportItemFromAssignment(
    template,
    {
      assignment_number: 1,
      location_ids: [],
      preparer: [],
      approver: [],
      sign_off_approvers: [],
      enforceThreshold: !!template.enforceThreshold,
      auto_approve: !!template.auto_approve,
      glcodes: normalizeGlcodeList(template.glcode),
    },
    options
  );
}

/**
 * One Excel row per assignment_number (location / preparer / approver / glcode vary per row).
 */
export function templateToBulkEditExportItems(
  template = {},
  options = {}
) {
  const locationArr = Array.isArray(template.location)
    ? template.location.filter(Boolean)
    : [];
  const taskType = template.task_type || template.taskType || '';
  const assignments = locationArr.length
    ? groupLocationsByAssignmentNumber(locationArr, taskType)
    : assignmentsFromTemplate(template);

  if (!assignments.length) {
    return [
      buildBulkEditExportItemFromAssignment(
        template,
        {
          assignment_number: 1,
          location_ids: [],
          preparer: [],
          approver: [],
          sign_off_approvers: [],
          enforceThreshold: !!template.enforceThreshold,
          auto_approve: !!template.auto_approve,
          glcodes: normalizeGlcodeList(template.glcode),
        },
        options
      ),
    ];
  }

  return assignments.map((assignment) =>
    buildBulkEditExportItemFromAssignment(template, assignment, options)
  );
}

/** XLSX cell with bold font for export headers. */
function boldHeader(text) {
  return { v: text, s: { font: { bold: true } } };
}

/**
 * Column defs for exportTasklistExcel (bulk-edit download), including custom fields.
 */
function buildBulkEditExportColumns(
  customColumns = [],
  taskTypes,
  locationHeader = 'Location'
) {
  const AR = taskTypes?.ACCOUNT_RECONCILIATION || 'Account Reconciliation';
  const locLabel = locationHeader || 'Location';

  const columns = [
    { header: boldHeader('Reference'), value: 'ref' },
    {
      header: boldHeader('Task Name'),
      value: 'description',
      maxWidth: EXPORT_TASK_NAME_MAX_WIDTH,
      minWidth: 20,
      wrapText: true,
    },
    { header: boldHeader('Due Days'), value: 'duedays' },
    { header: boldHeader('Category'), value: 'category' },
    { header: boldHeader('Sub Category'), value: 'subcategory' },
    { header: boldHeader('Priority'), value: 'priority' },
    {
      header: boldHeader('Preparer'),
      value: (task) => task.preparerEmails || '',
    },
    {
      header: boldHeader('Approver'),
      value: (task) => task.approverEmails || '',
    },
    {
      header: boldHeader(SIGN_OFF.APPROVER_LABEL),
      value: (task) => task.signOffApproverEmails || '',
    },
    {
      header: boldHeader('Purpose'),
      value: (task) => task.purpose || '',
    },
    {
      header: boldHeader('Instruction'),
      value: (task) => task.instruction || '',
    },
    {
      header: boldHeader('Frequency'),
      value: (task) => (Array.isArray(task.tags) ? task.tags.join(', ') : task.tags || ''),
    },
    {
      header: boldHeader('No. of Evidences'),
      value: (task) => task.no_of_evidence || 0,
    },
    {
      header: boldHeader(EVIDENCE_REQUIRED.EXCEL_HEADER),
      value: (task) => (isEvidenceRequiredEnabled(task.evidence_required) ? 'Yes' : 'No'),
    },
    {
      header: boldHeader('Account No'),
      value: (task) =>
        Array.isArray(task.glcode) ? task.glcode.join(', ') : task.glcode || '',
    },
    {
      header: boldHeader(locLabel),
      value: (task) =>
        Array.isArray(task.location) ? task.location.join(', ') : task.location || '',
    },
    {
      header: boldHeader('Assertions'),
      value: (task) =>
        Array.isArray(task.assertions) ? task.assertions.join(', ') : task.assertions || '',
    },
    {
      header: boldHeader('Enforce Threshold'),
      value: (task) =>
        task.task_type === AR ||
        String(task.task_type || '').toLowerCase().includes('reconcil')
          ? task.enforceThreshold === false
            ? 'No'
            : 'Yes'
          : 'N/A',
    },
    {
      header: boldHeader(AUTO_APPROVE.EXCEL_HEADER),
      value: (task) => (task.auto_approve ? 'Yes' : 'No'),
    },
  ];

  getActiveCustomColumns(customColumns).forEach((column) => {
    columns.push({
      header: boldHeader(customColumnHeaderText(column)),
      value: (task) => {
        const val = task[column.columnName];
        if (column.dataType === 'date' && val) {
          const d = val instanceof Date ? val : new Date(val);
          if (!Number.isNaN(d.getTime())) return d.toLocaleDateString('en-GB');
        }
        return val == null ? '' : val;
      },
    });
  });

  return columns;
}

/**
 * Download bulk-edit template using the same exportTasklistExcel layout as before:
 * title "Tasklist List", sheet "TOP SHEET", bold headers, selected rows only.
 */
export async function downloadBulkEditTemplate({
  templates = [],
  users = [],
  locations = [],
  customColumns = [],
  cycleType = 'FBC',
  filename,
  title = 'Tasklist List',
  taskTypes,
  locationHeader = 'Location',
} = {}) {
  const cycle = (cycleType || '').toUpperCase();
  const locLabel = locationHeader || 'Location';
  const exportTemplates = (templates || []).filter((t) => {
    const tCycle = (t.cycle_type || t.cycleType || '').toUpperCase();
    return !cycle || !tCycle || tCycle === cycle;
  });

  if (!exportTemplates.length) {
    throw new Error('No selected tasks available to export');
  }

  const taskItems = [];
  exportTemplates.forEach((template) => {
    taskItems.push(
      ...templateToBulkEditExportItems(template, {
        users,
        locations,
        customColumns,
        taskTypes,
      })
    );
  });

  await exportTasklistExcel({
    title,
    filename: filename || `Bulk_Edit_Task_List_${cycle || 'ALL'}.xlsx`,
    sheetName: 'TOP SHEET',
    taskItems,
    columns: buildBulkEditExportColumns(customColumns, taskTypes, locLabel),
    instructions: getBulkEditTaskTemplateInstructions(locLabel),
    instructionsSheetName: 'Instruction',
  });
}

/**
 * Merge bulk-edit rows that share the same ref into one API payload row.
 */
export function mergeBulkEditPayloadByRef(rows = [], customColumns = []) {
  const map = new Map();
  const customKeys = getActiveCustomColumns(customColumns).map((c) => c.columnName);

  (rows || []).forEach((row) => {
    const ref = String(row.ref || '').trim();
    if (!ref) return;

    if (!map.has(ref)) {
      map.set(ref, {
        ...row,
        locationNames: [...(row.locationNames || [])],
        customfields: Array.isArray(row.customfields) ? [...row.customfields] : row.customfields,
      });
      return;
    }

    const existing = map.get(ref);
    (row.locationNames || []).forEach((name) => {
      if (name && !existing.locationNames.includes(name)) {
        existing.locationNames.push(name);
      }
    });
    BULK_MERGE_SCALAR_KEYS.forEach((key) => {
      if (row[key] != null && row[key] !== '') existing[key] = row[key];
    });
    if (row.duedays != null) existing.duedays = row.duedays;
    if (row.no_of_evidence != null) existing.no_of_evidence = row.no_of_evidence;
    if (row.evidence_required != null) existing.evidence_required = row.evidence_required;
    if (Array.isArray(row.glcode) && row.glcode.length) existing.glcode = row.glcode;
    if (Array.isArray(row.tags) && row.tags.length) existing.tags = row.tags;
    if (Array.isArray(row.assertions) && row.assertions.length) existing.assertions = row.assertions;
    customKeys.forEach((key) => {
      if (row[key] != null && row[key] !== '') existing[key] = row[key];
    });
    if (Array.isArray(row.customfields) && row.customfields.length) {
      existing.customfields = row.customfields;
    }
  });

  return [...map.values()];
}

/**
 * Merge bulk-import rows that share the same description into one task,
 * combining locations (one location per Excel row convention).
 */
export function mergeImportPayloadByDescription(rows = [], customColumns = []) {
  const map = new Map();
  const customKeys = getActiveCustomColumns(customColumns).map((c) => c.columnName);

  (rows || []).forEach((row) => {
    const key = String(row.description || '')
      .trim()
      .toLowerCase();
    if (!key) return;

    if (!map.has(key)) {
      const locs = [...(row.location || row.locationNames || [])].filter(Boolean);
      map.set(key, {
        ...row,
        location: locs,
        locationNames: [...locs],
        customfields: Array.isArray(row.customfields) ? [...row.customfields] : row.customfields,
      });
      return;
    }

    const existing = map.get(key);
    const locs = [...(row.location || row.locationNames || [])].filter(Boolean);
    locs.forEach((name) => {
      if (!existing.location.includes(name)) existing.location.push(name);
      if (!existing.locationNames.includes(name)) existing.locationNames.push(name);
    });
    BULK_MERGE_SCALAR_KEYS.forEach((fieldKey) => {
      if (row[fieldKey] != null && row[fieldKey] !== '') existing[fieldKey] = row[fieldKey];
    });
    if (row.duedays != null) existing.duedays = row.duedays;
    if (row.no_of_evidence != null) existing.no_of_evidence = row.no_of_evidence;
    if (row.evidence_required != null) existing.evidence_required = row.evidence_required;
    if (Array.isArray(row.glcode) && row.glcode.length) existing.glcode = row.glcode;
    if (Array.isArray(row.tags) && row.tags.length) existing.tags = row.tags;
    customKeys.forEach((colKey) => {
      if (row[colKey] != null && row[colKey] !== '') existing[colKey] = row[colKey];
    });
    if (Array.isArray(row.customfields) && row.customfields.length) {
      existing.customfields = row.customfields;
    }
  });

  return [...map.values()];
}

/**
 * Parse blank-template (import) Excel rows into payload + preview.
 * Matches rows to existing templates by ref/description when present.
 */
function parseImportFormat(sheetRows, headerIndex, ctx) {
  const {
    taskTypes,
    byRef,
    byDescription,
    customColumns = [],
    locationHeader = 'Location',
  } = ctx;
  const NORMAL = taskTypes?.NORMAL || FALLBACK_TASK_TYPES.NORMAL;
  const AR = taskTypes?.ACCOUNT_RECONCILIATION || FALLBACK_TASK_TYPES.ACCOUNT_RECONCILIATION;
  const locLabel = locationHeader || 'Location';
  const headerRow = normalizeHeaderRow(sheetRows[headerIndex]);
  const hasRefColumn = ['ref', 'reference'].includes(
    String(headerRow[0] || '').toLowerCase()
  );
  const dataHeader = hasRefColumn ? headerRow.slice(1) : headerRow;
  const expectedHeaders = getBulkImportExcelHeaders(locLabel);

  if (!importHeadersMatchWithLocationAlias(dataHeader, getBulkImportExcelHeaders, locLabel)) {
    throw new Error(
      `Columns '${expectedHeaders.join("','")}' are required in the same order`
    );
  }

  const payload = [];
  const preview = [];

  for (let i = headerIndex + 1; i < sheetRows.length; i += 1) {
    const raw = sheetRows[i] || [];
    if (!raw.length || raw.every((c) => c == null || String(c).trim() === '')) continue;

    let ref = hasRefColumn ? String(raw[0] || '').trim() : '';
    const el = hasRefColumn ? raw.slice(1) : raw;

    const description = (el[0] || '').toString().trim();
    const category = (el[1] || '').toString().trim();
    const subcategory = (el[2] || '').toString().trim();
    const location = splitLocationNames(el[3]);
    const assertions = splitCsv(el[4]);
    const purpose = el[5] || '';
    const instruction = el[6] || '';
    const preparer = splitCsv(el[7]);
    const approver = splitCsv(el[8]);
    const sign_off_approver = splitCsv(el[9]);
    const tags = splitCsv(el[10]);
    const duedays = el[11] === '' || el[11] == null ? null : Number(el[11]);
    const glcode = normalizeGlcodeList(splitCsv(el[12]));
    const priority = (el[13] || '').toString().trim();
    const dataHeader = hasRefColumn ? headerRow.slice(1) : headerRow;
    const { no_of_evidence, evidence_required } = readEvidenceCells(el, dataHeader, 14, -1);
    const enforceIndex = headerIndexByLabel(dataHeader, 'Enforce Threshold');
    const autoIndex = headerIndexByLabel(dataHeader, AUTO_APPROVE.EXCEL_HEADER);
    const enforceThreshold = parseEnforceThresholdCell(
      cellAt(el, enforceIndex >= 0 ? enforceIndex : 16)
    );
    const auto_approve = parseYesNoBoolean(cellAt(el, autoIndex >= 0 ? autoIndex : 17));

    const { values: customValues, customfields } = readCustomFieldValues(
      raw,
      headerRow,
      customColumns
    );

    let matched = ref ? byRef.get(ref) : null;
    if (!matched && description) matched = byDescription.get(description.toLowerCase()) || null;
    // Bulk import preview ref comes from bulkvalidate rowMeta — do not copy library ref by name.

    const task_type = glcode.length ? AR : NORMAL;
    let row = {
      description,
      category,
      subcategory,
      purpose,
      instruction,
      tags,
      assertions,
      duedays: Number.isFinite(duedays) ? duedays : null,
      glcode,
      priority,
      no_of_evidence,
      evidence_required,
      enforceThreshold: task_type === AR ? !!enforceThreshold : false,
      auto_approve,
      task_type,
      location,
      preparer,
      approver,
      sign_off_approver,
      requires_sign_off: sign_off_approver.length > 0,
    };
    row = applyCustomFieldValues(row, customValues, customfields);
    if (ref && hasRefColumn) row.ref = ref;
    if (matched?._id) row._id = matched._id;

    payload.push(row);
    preview.push(buildPreviewRow(row, customColumns));
  }

  return { payload, preview };
}

/**
 * Parse bulk-edit Excel rows (Reference + Task Name layout) into payload + preview.
 * Skips rows without a ref after matching.
 * Accepts legacy "Task Description" / "Description" / "Tags" headers on upload.
 */
function parseBulkEditFormat(sheetRows, headerIndex, ctx) {
  const {
    taskTypes,
    byRef,
    byDescription,
    customColumns = [],
    locationHeader = 'Location',
  } = ctx;
  const NORMAL = taskTypes?.NORMAL || FALLBACK_TASK_TYPES.NORMAL;
  const AR = taskTypes?.ACCOUNT_RECONCILIATION || FALLBACK_TASK_TYPES.ACCOUNT_RECONCILIATION;
  const locLabel = locationHeader || 'Location';
  const headerRow = normalizeHeaderRow(sheetRows[headerIndex]);
  const offset = findBulkEditRefColumnIndex(headerRow);
  const dataHeader = headerRow.slice(offset);
  const expectedHeaders = getBulkEditExcelHeaders(locLabel);

  const firstOk = ['ref', 'reference'].includes(
    String(dataHeader[0] || '').toLowerCase()
  );
  const secondHeader = String(dataHeader[1] || '')
    .trim()
    .toLowerCase();
  const secondOk =
    secondHeader === 'task name' ||
    secondHeader.includes('description');
  if (!firstOk || !secondOk) {
    throw new Error(
      `Columns '${expectedHeaders.join("','")}' are required in the same order`
    );
  }

  // Relative to Reference column (handles empty leading col from exportTasklistExcel)
  const col = {
    ref: offset + 0,
    description: offset + 1,
    duedays: offset + 2,
    category: offset + 3,
    subcategory: offset + 4,
    priority: offset + 5,
    preparer: offset + 6,
    approver: offset + 7,
    sign_off_approver: offset + 8,
    purpose: offset + 9,
    instruction: offset + 10,
    tags: offset + 11,
    evidences: offset + 12,
    accountNo: offset + 14,
    location: offset + 15,
    enforceThreshold: offset + 17,
    autoApprove: offset + 18,
  };

  const payload = [];
  const preview = [];

  for (let i = headerIndex + 1; i < sheetRows.length; i += 1) {
    const raw = sheetRows[i] || [];
    if (!raw.length || raw.every((c) => c == null || String(c).trim() === '')) continue;

    let ref = String(raw[col.ref] || '').trim();
    const description = String(raw[col.description] || '').trim();
    const duedaysRaw = raw[col.duedays];
    const duedays = duedaysRaw === '' || duedaysRaw == null ? null : Number(duedaysRaw);
    const category = String(raw[col.category] || '').trim();
    const subcategory = String(raw[col.subcategory] || '').trim();
    const priority = String(raw[col.priority] || '').trim();
    const preparer = splitCsv(raw[col.preparer]);
    const approver = splitCsv(raw[col.approver]);
    const sign_off_approver = splitCsv(raw[col.sign_off_approver]);
    const purpose = raw[col.purpose] || '';
    const instruction = raw[col.instruction] || '';
    const tags = splitCsv(raw[col.tags]);
    const { no_of_evidence, evidence_required } = readEvidenceCells(
      raw,
      headerRow,
      col.evidences,
      -1
    );
    const accountIndex = headerIndexByLabel(headerRow, 'Account No');
    const locationIndex = headerIndexByLabel(headerRow, locLabel);
    const locationFallbackIndex =
      locationIndex < 0 && locLabel !== 'Location'
        ? headerIndexByLabel(headerRow, 'Location')
        : locationIndex;
    const enforceIndex = headerIndexByLabel(headerRow, 'Enforce Threshold');
    const autoIndex = headerIndexByLabel(headerRow, AUTO_APPROVE.EXCEL_HEADER);
    const glcode = normalizeGlcodeList(
      splitCsv(cellAt(raw, accountIndex >= 0 ? accountIndex : col.accountNo))
    );
    const locationNames = splitLocationNames(
      cellAt(raw, locationFallbackIndex >= 0 ? locationFallbackIndex : col.location)
    );
    const assertionsIndex = headerIndexByLabel(headerRow, 'Assertions');
    const assertions = assertionsIndex >= 0
      ? splitCsv(cellAt(raw, assertionsIndex))
      : [];
    const enforceThreshold = parseEnforceThresholdCell(
      cellAt(raw, enforceIndex >= 0 ? enforceIndex : col.enforceThreshold)
    );
    const auto_approve = parseYesNoBoolean(
      cellAt(raw, autoIndex >= 0 ? autoIndex : col.autoApprove)
    );

    const { values: customValues, customfields } = readCustomFieldValues(
      raw,
      headerRow,
      customColumns
    );

    let matched = ref ? byRef.get(ref) : null;
    if (!matched && description) matched = byDescription.get(description.toLowerCase()) || null;
    if (!ref && matched?.ref) ref = String(matched.ref);

    const task_type =
      glcode.length ||
      matched?.task_type === AR ||
      matched?.task_type === 'Account Reconciliation'
        ? AR
        : NORMAL;
    let row = {
      ref,
      description,
      category,
      subcategory,
      purpose,
      instruction,
      tags,
      assertions,
      duedays: Number.isFinite(duedays) ? duedays : null,
      glcode,
      priority,
      no_of_evidence,
      evidence_required,
      enforceThreshold: task_type === AR ? !!enforceThreshold : false,
      auto_approve,
      task_type,
      location: locationNames,
      preparer,
      approver,
      sign_off_approver,
      requires_sign_off: sign_off_approver.length > 0,
    };
    row = applyCustomFieldValues(row, customValues, customfields);
    if (matched?._id) row._id = matched._id;
    if (!ref) continue;

    payload.push(row);
    preview.push(buildPreviewRow(row, customColumns));
  }

  return { payload, preview };
}

/**
 * Parse Excel → one payload/preview row per Excel data row (no location expand/merge).
 * Server owns business validation via bulkvalidate (mode: create | update).
 */
export function parseBulkImportExcel(
  sheetRows = [],
  {
    mode = BULK_IMPORT_MODES.IMPORT,
    taskTypes,
    templates = [],
    customColumns = [],
    cycleType,
    locationHeader = 'Location',
  } = {}
) {
  if (!sheetRows?.length) throw new Error('Excel file is empty');

  const locLabel = locationHeader || 'Location';
  const { byRef, byDescription } = buildTemplateLookup(templates);
  const headerIndex = findHeaderRowIndex(sheetRows);
  const format = detectSheetFormat(normalizeHeaderRow(sheetRows[headerIndex]));
  const ctx = { mode, taskTypes, byRef, byDescription, customColumns, locationHeader: locLabel };

  let result;
  if (format === 'bulkEdit') {
    result = parseBulkEditFormat(sheetRows, headerIndex, ctx);
  } else if (format === 'import') {
    result = parseImportFormat(sheetRows, headerIndex, ctx);
  } else {
    throw new Error(
      mode === BULK_IMPORT_MODES.BULK_EDIT
        ? `Columns '${getBulkEditExcelHeaders(locLabel).join("','")}' are required in the same order`
        : `Columns '${getBulkImportExcelHeaders(locLabel).join("','")}' are required in the same order`
    );
  }

  if (!result.payload.length) {
    throw new Error('No valid rows found in the Excel file');
  }

  if (mode === BULK_IMPORT_MODES.BULK_EDIT) {
    const unresolved = result.payload.filter((row) => !row.ref);
    if (unresolved.length === result.payload.length) {
      throw new Error(
        'Could not match any rows to existing tasks. Use Download template to export your current tasks (with Reference), edit that file, then upload it.'
      );
    }
    result.payload = result.payload.filter((row) => row.ref);
  }

  const cycle = (cycleType || '').toUpperCase();
  const rows = (result.payload || []).map((row) => {
    const next = { ...row };
    if (cycle && !next.cycle_type) next.cycle_type = cycle;
    // Ensure location is always a string[] (never a single comma-joined string)
    next.location = asLocationArray(next.location || next.locationNames);
    delete next.locationNames;
    delete next._preparerEmails;
    delete next._approverEmails;
    delete next._signOffApproverEmails;
    return next;
  });

  const preview = rows.map((row) => {
    const built = buildPreviewRow(row, customColumns);
    if (mode !== BULK_IMPORT_MODES.BULK_EDIT) {
      return { ...built, ref: '—', _errors: {} };
    }
    return { ...built, _errors: {} };
  });

  return {
    payload: rows,
    preview,
    rowErrors: rows.map(() => ({})),
  };
}

/** Normalize location to string[] without breaking legal-entity suffixes. */
export function asLocationArray(value) {
  return splitLocationNames(value);
}

/**
 * Strip UI-only fields and normalize arrays for bulkvalidate / bulkadd body.
 * One Excel row → one object (same order).
 */
export function toBulkApiPayloadRows(rows = [], { mode, cycleType } = {}) {
  const cycle = (cycleType || '').toUpperCase();
  const isEdit = mode === BULK_IMPORT_MODES.BULK_EDIT;

  return (rows || []).map((row) => {
    const location = asLocationArray(row.location || row.locationNames);
    const preparer = Array.isArray(row.preparer)
      ? row.preparer
      : Array.isArray(row._preparerEmails)
        ? row._preparerEmails
        : splitCsv(row.preparer);
    const approver = Array.isArray(row.approver)
      ? row.approver
      : Array.isArray(row._approverEmails)
        ? row._approverEmails
        : splitCsv(row.approver);
    const glcode = normalizeGlcodeList(
      Array.isArray(row.glcode) ? row.glcode : splitCsv(row.glcode)
    );
    const tags = Array.isArray(row.tags) ? row.tags : splitCsv(row.tags);
    const assertions = Array.isArray(row.assertions)
      ? row.assertions
      : splitCsv(row.assertions);
    const signOffApprover = Array.isArray(row.sign_off_approver)
      ? row.sign_off_approver
      : Array.isArray(row.sign_off_approvers)
        ? row.sign_off_approvers
        : splitCsv(row.sign_off_approver || row.sign_off_approvers);
    const AR = FALLBACK_TASK_TYPES.ACCOUNT_RECONCILIATION;
    const taskType =
      row.task_type || (glcode.length ? AR : FALLBACK_TASK_TYPES.NORMAL);

    const next = {
      description: row.description || '',
      category: row.category || '',
      subcategory: row.subcategory || '',
      location,
      preparer: preparer.filter(Boolean),
      approver: approver.filter(Boolean),
      sign_off_approvers: signOffApprover.filter(Boolean),
      glcode,
      purpose: row.purpose || '',
      instruction: row.instruction || '',
      tags,
      assertions,
      duedays: row.duedays == null || row.duedays === '' ? null : Number(row.duedays),
      priority: row.priority || '',
      no_of_evidence:
        row.no_of_evidence == null || row.no_of_evidence === ''
          ? 0
          : Number(row.no_of_evidence),
      evidence_required: coerceEvidenceRequired(row.evidence_required),
      enforceThreshold: taskType === AR ? !!row.enforceThreshold : false,
      auto_approve: !!row.auto_approve,
    };

    if (typeof row.requires_sign_off === 'boolean') {
      next.requires_sign_off = row.requires_sign_off;
    } else if (signOffApprover.length) {
      next.requires_sign_off = true;
    }

    if (row.task_type) next.task_type = row.task_type;
    else if (glcode.length) next.task_type = AR;

    if (cycle) next.cycle_type = row.cycle_type || cycle;
    if (isEdit && row.ref) next.ref = row.ref;
    if (Array.isArray(row.customfields) && row.customfields.length) {
      next.customfields = row.customfields;
    } else {
      // Flatten top-level custom keys into customfields when present
      const customKeys = Object.keys(row).filter(
        (k) => !BULK_IMPORT_ROW_SYSTEM_KEYS.includes(k) && !k.startsWith('_')
      );
      if (customKeys.length) {
        next.customfields = customKeys.map((k) => ({ [k]: row[k] }));
        customKeys.forEach((k) => {
          next[k] = row[k];
        });
      }
    }

    return next;
  });
}

/** Map server field keys onto preview column values. */
function mapServerFieldErrors(fields = {}) {
  const mapped = { ...(fields || {}) };

  if (mapped.no_of_evidence && !mapped.evidences) {
    mapped.evidences = mapped.no_of_evidence;
  }
  if (
    mapped.evidence_required &&
    String(mapped.evidence_required).toLowerCase().includes('evidences') &&
    !mapped.evidences
  ) {
    mapped.evidences = mapped.evidence_required;
  }

  if (mapped.sign_off_approvers && !mapped.sign_off_approver) {
    mapped.sign_off_approver = mapped.sign_off_approvers;
  }

  // A subcategory COA message must not stay on the category column.
  if (
    mapped.category &&
    /sub\s*categor/i.test(String(mapped.category)) &&
    !mapped.subcategory
  ) {
    mapped.subcategory = mapped.category;
    delete mapped.category;
  }

  // Backend often puts group/GL linkage messages on `_row`
  if (mapped._row) {
    const msg = String(mapped._row);
    const lower = msg.toLowerCase();
    if (!mapped.glcode && (lower.includes('gl') || lower.includes('account'))) {
      mapped.glcode = msg;
    } else if (!mapped.location && lower.includes('location')) {
      mapped.location = msg;
    } else if (!mapped.preparer && lower.includes('preparer')) {
      mapped.preparer = msg;
    } else if (!mapped.approver && lower.includes('approver') && !lower.includes('sign-off')) {
      mapped.approver = msg;
    } else if (
      !mapped.sign_off_approver &&
      (lower.includes('sign-off') || lower.includes('sign off'))
    ) {
      mapped.sign_off_approver = msg;
    } else if (!mapped.ref && (lower.includes('reference') || lower.includes('ref'))) {
      mapped.ref = msg;
    } else if (!mapped.subcategory && lower.includes('sub categor')) {
      mapped.subcategory = msg;
    } else if (!mapped.category && lower.includes('categor')) {
      mapped.category = msg;
    } else if (!mapped.description) {
      mapped.description = msg;
    }
  }

  return mapped;
}

/**
 * Bulk Import preview only — apply server `rowMeta[].ref` onto preview rows by rowIndex.
 * Display-only; not sent on bulkadd.
 *
 * @param {Array} previewRows
 * @param {Array<{ rowIndex: number, ref: string }>} rowMeta
 * @returns {Array}
 */
export function applyValidationRowMeta(previewRows = [], rowMeta = []) {
  if (!Array.isArray(previewRows) || !previewRows.length) {
    return previewRows || [];
  }

  const refByIndex = {};
  (rowMeta || []).forEach((entry) => {
    const idx = entry?.rowIndex;
    if (idx == null || idx < 0) return;
    const ref = entry?.ref ?? entry?.previewRef;
    if (ref != null && ref !== '') {
      refByIndex[idx] = String(ref);
    }
  });

  return previewRows.map((row, idx) => ({
    ...row,
    ref: refByIndex[idx] != null ? refByIndex[idx] : '—',
  }));
}

/** Map server `rowErrors[].fields` onto preview `_errors` by rowIndex. */
export function applyServerRowErrors(previewRows = [], serverRowErrors = []) {
  const errorsByIndex = {};
  (serverRowErrors || []).forEach((entry) => {
    const idx = entry?.rowIndex;
    if (idx == null || idx < 0) return;
    const mapped = mapServerFieldErrors(entry.fields || {});
    errorsByIndex[idx] = {
      ...(errorsByIndex[idx] || {}),
      ...mapped,
    };
  });

  const rowErrors = (previewRows || []).map((_, idx) => errorsByIndex[idx] || {});
  const preview = (previewRows || []).map((row, idx) => ({
    ...row,
    _errors: errorsByIndex[idx] || {},
  }));

  return {
    preview,
    rowErrors,
    hasErrors: rowErrors.some((e) => e && Object.keys(e).length > 0),
  };
}
